import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { storeCompanySecrets, getCompanySecretsMetadata } from '../../services/api.js';
import './TripletexIntegration.css';

/**
 * Tripletex Integration settings component
 * Allows admins to configure Tripletex Employee Token (ETOKEN)
 * Consumer Token (CTOKEN) is managed centrally
 * @returns {JSX.Element} TripletexIntegration component
 */
const TripletexIntegration = () => {
  const { t } = useTranslation();
  const { activeCompany } = useAuth();
  const [formData, setFormData] = useState({
    etoken: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [secretsMetadata, setSecretsMetadata] = useState([]);
  const [isLoadingSecrets, setIsLoadingSecrets] = useState(true);

  // Check if current user is admin for this company
  const isAdmin = activeCompany?.role === 'admin';

  // Filter secrets for Tripletex integration
  const tripletexSecrets = secretsMetadata.filter(secret => secret.integration_type === 'tripletex');
  
  // Check if we have existing secrets (determines if this is an update or initial setup)
  const hasExistingSecrets = tripletexSecrets.some(secret => secret.is_active);

  // Load secrets metadata on component mount
  useEffect(() => {
    const loadSecretsMetadata = async () => {
      if (!activeCompany?.id) {
        setIsLoadingSecrets(false);
        return;
      }

      try {
        setIsLoadingSecrets(true);
        const response = await getCompanySecretsMetadata(activeCompany.id);
        setSecretsMetadata(response.secrets || []);
      } catch (error) {
        console.error('Error loading secrets metadata:', error);
        // Don't show error message for metadata loading - it's not critical
      } finally {
        setIsLoadingSecrets(false);
      }
    };

    loadSecretsMetadata();
  }, [activeCompany?.id]);

  // Function to refresh secrets metadata after successful submission
  const refreshSecretsMetadata = async () => {
    if (!activeCompany?.id) return;
    
    try {
      const response = await getCompanySecretsMetadata(activeCompany.id);
      setSecretsMetadata(response.secrets || []);
    } catch (error) {
      console.error('Error refreshing secrets metadata:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAdmin) {
      setMessage({ type: 'error', text: t('settings.tripletex.adminNotice') });
      return;
    }

    if (!formData.etoken.trim()) {
      setMessage({ type: 'error', text: t('errors.requiredField') });
      return;
    }

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      // Prepare secrets array - only ETOKEN is required (CTOKEN is managed centrally)
      const secrets = [
        { name: 'ETOKEN', value: formData.etoken.trim() }
      ];

      // Call API to store secrets
      const result = await storeCompanySecrets('tripletex', secrets, activeCompany.id);
      
      // Create appropriate success message based on what was created vs updated
      let messageText;
      if (result.created_secrets && result.updated_secrets && result.created_secrets.length > 0 && result.updated_secrets.length > 0) {
        messageText = t('settings.tripletex.createdAndUpdated', { created: result.created_secrets.length, updated: result.updated_secrets.length });
      } else if (result.created_secrets && result.created_secrets.length > 0) {
        messageText = t('settings.tripletex.keysCreated', { count: result.created_secrets.length });
      } else if (result.updated_secrets && result.updated_secrets.length > 0) {
        messageText = t('settings.tripletex.keysUpdated', { count: result.updated_secrets.length });
      } else {
        messageText = result.message || t('settings.tripletex.keysProcessed');
      }
      
      setMessage({ 
        type: 'success', 
        text: messageText
      });
      
      // Clear form after successful submission
      setFormData({ etoken: '' });
      
      // Refresh secrets metadata to update status cards
      await refreshSecretsMetadata();
      
    } catch (error) {
      console.error('Error saving Tripletex tokens:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || t('settings.tripletex.saveError') 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const locale = i18n.language === 'no' ? 'no-NO' : 'en-US';
  
  return (
    <div className="tripletex-integration">
      <div className="integration-header">
        <h2>{t('settings.tripletex.title')}</h2>
        <p className="integration-description">
          {t('settings.tripletex.description')}
        </p>
      </div>

      {/* Status Cards */}
      {!isLoadingSecrets && (
        <div className="secrets-status">
          <h3 className="status-title">{t('settings.tripletex.currentConfig')}</h3>
          {tripletexSecrets.length > 0 ? (
            <div className="status-cards">
              {tripletexSecrets
                .filter(secret => secret.is_active)
                .map((secret, index) => (
                  <div key={`${secret.name}-${index}`} className="status-card">
                    <div className="status-card-header">
                      <div className="status-card-name">{secret.name}</div>
                      <div className="status-badge active">{t('settings.tripletex.active')}</div>
                    </div>
                    <div className="status-card-details">
                      <div className="status-card-description">{secret.description}</div>
                      <div className="status-card-date">
                        {t('settings.tripletex.configured')} {new Date(secret.created_at).toLocaleDateString(locale)}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="no-secrets-message">
              <p>{t('settings.tripletex.noKeys')}</p>
              <p className="no-secrets-subtitle">{t('settings.tripletex.useForm')}</p>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="integration-form">
        <div className="form-group">
          <label htmlFor="etoken" className="form-label">
            {t('settings.tripletex.employeeToken')}
          </label>
          <input
            type="password"
            id="etoken"
            name="etoken"
            value={formData.etoken}
            onChange={handleInputChange}
            className="form-input"
            placeholder={t('settings.tripletex.tokenPlaceholder')}
            disabled={isSubmitting}
          />
          <p className="field-help">
            {t('settings.tripletex.tokenHelp')}
          </p>
        </div>

        {/* Message display */}
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Submit button - visible to all, enabled only for admins */}
        <div className="form-actions">
          <button
            type="submit"
            className={`submit-button ${!isAdmin ? 'disabled' : ''}`}
            disabled={!isAdmin || isSubmitting}
            title={!isAdmin ? t('settings.tripletex.adminNotice') : ''}
          >
            {isSubmitting ? t('settings.tripletex.saving') : hasExistingSecrets ? t('settings.tripletex.updateKeys') : t('settings.tripletex.saveKeys')}
          </button>
          
          {!isAdmin && (
            <p className="admin-notice">
              {t('settings.tripletex.adminNotice')}
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

export default TripletexIntegration;
